﻿import { createContext } from 'react';

import type { Client } from './types';

export interface ClientContextValue {
    /** All accessible clients loaded from the API. Empty while loading or on error. */
    clients: Client[];
    /** The currently selected client, or null while loading. */
    selectedClient: Client | null;
    isLoading: boolean;
    isError: boolean;
    /** True when the authenticated user has the OriginUser role and can switch between all clients. */
    isOriginUser: boolean;
    /**
     * True when the user may see vehicle locations (Origin user AND the per-user
     * vehicle-tracking permission). Gates the vans layer, its Layers-panel toggle and
     * all vehicle polling; the backend independently enforces every request.
     */
    canViewVehicleTracking: boolean;
    /**
     * Flips the vehicle-tracking capability off for this session — called when a vehicle
     * request returns 403, meaning the permission was revoked server-side mid-session.
     * Consumers must also drop any cached vehicle state when this fires.
     */
    revokeVehicleTracking: () => void;
    /**
     * True when the user may enter Network Explorer, the cross-client viewing mode (Origin
     * user AND the per-user permission). Gates the switcher entry, the route and the default
     * landing; the backend independently enforces every explorer request.
     */
    canViewNetworkExplorer: boolean;
    /**
     * Switches the active client. Updates context and localStorage immediately
     * without a page reload so downstream components re-render.
     */
    switchClient: (clientId: string) => void;
}

export const ClientContext = createContext<ClientContextValue | null>(null);
